import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import SEO from "@/lib/seo";
import { PageHero } from "@/components/Primitives";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back.");
      const next = params.get("next") || "/dashboard";
      navigate(next);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page">
      <SEO title="Sign in · The Cleanability Institute" description="Access your member dashboard." path="/login" />
      <PageHero testId="login-hero" eyebrow="Sign in" title="Return to the Institute." lead="Members access the dashboard, certifications, and research subscriptions here." />
      <section className="container-x pb-24">
        <div className="max-w-md">
          <form onSubmit={submit} data-testid="login-form" className="rounded-3xl border border-border bg-card p-8 space-y-5">
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="login-email" />
            <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testId="login-password" />
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" />
            </button>
            <div className="text-xs text-muted-foreground text-center">
              No account? <Link to="/register" data-testid="login-to-register" className="underline-link font-medium text-foreground">Create one</Link>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testId }) {
  return (
    <label className="block">
      <div className="text-xs font-mono-mini text-muted-foreground">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
      />
    </label>
  );
}
